/**
 * Curated font catalogue.
 *
 * We deliberately load the *binary TTF* from the google/fonts repository via jsDelivr
 * (CORS-enabled) instead of going through the Google Fonts CSS API, for three reasons:
 *
 *  1. The CSS API serves WOFF2, which opentype.js cannot parse without an external
 *     Brotli decompressor.
 *  2. The CSS API serves unicode-range *subsets*; the repo TTFs are complete.
 *  3. One and the same binary then feeds both the rasteriser and the outline
 *     extraction, so the alpha mask can never drift from the vector contours.
 *
 * Filenames were verified against the repository; `axes` is only a hint for the UI
 * before the font is loaded — the authoritative axis list comes from the parsed `fvar`
 * table.
 */

export type FontCategory = 'sans' | 'serif' | 'mono' | 'display' | 'slab' | 'blackletter' | 'experimental';

export interface CatalogFont {
  /** Stable id used in presets and share links. */
  id: string;
  family: string;
  category: FontCategory;
  /** Path inside the google/fonts repository. */
  path: string;
  /** Variable axis tags, if the file is a variable font. */
  axes?: string[];
  /** Short note shown in the picker. */
  note?: string;
}

const GH_PIN = 'main';

export function fontUrl(font: CatalogFont): string {
  const encoded = font.path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `https://cdn.jsdelivr.net/gh/google/fonts@${GH_PIN}/${encoded}`;
}

export const FONT_CATALOG: CatalogFont[] = [
  // ── Sans ────────────────────────────────────────────────────────────────────
  { id: 'inter', family: 'Inter', category: 'sans', path: 'ofl/inter/Inter[opsz,wght].ttf', axes: ['opsz', 'wght'] },
  { id: 'archivo', family: 'Archivo', category: 'sans', path: 'ofl/archivo/Archivo[wdth,wght].ttf', axes: ['wdth', 'wght'], note: 'Breitenachse' },
  { id: 'outfit', family: 'Outfit', category: 'sans', path: 'ofl/outfit/Outfit[wght].ttf', axes: ['wght'] },
  { id: 'sora', family: 'Sora', category: 'sans', path: 'ofl/sora/Sora[wght].ttf', axes: ['wght'] },
  { id: 'chivo', family: 'Chivo', category: 'sans', path: 'ofl/chivo/Chivo[wght].ttf', axes: ['wght'] },
  { id: 'familjen-grotesk', family: 'Familjen Grotesk', category: 'sans', path: 'ofl/familjengrotesk/FamiljenGrotesk[wght].ttf', axes: ['wght'] },
  { id: 'space-grotesk', family: 'Space Grotesk', category: 'sans', path: 'ofl/spacegrotesk/SpaceGrotesk[wght].ttf', axes: ['wght'] },
  { id: 'oswald', family: 'Oswald', category: 'sans', path: 'ofl/oswald/Oswald[wght].ttf', axes: ['wght'], note: 'schmal' },
  { id: 'big-shoulders', family: 'Big Shoulders Display', category: 'sans', path: 'ofl/bigshouldersdisplay/BigShouldersDisplay[wght].ttf', axes: ['wght'], note: 'sehr schmal' },

  // ── Serif ───────────────────────────────────────────────────────────────────
  { id: 'playfair-display', family: 'Playfair Display', category: 'serif', path: 'ofl/playfairdisplay/PlayfairDisplay[wght].ttf', axes: ['wght'] },
  { id: 'bodoni-moda', family: 'Bodoni Moda', category: 'serif', path: 'ofl/bodonimoda/BodoniModa[opsz,wght].ttf', axes: ['opsz', 'wght'], note: 'hoher Kontrast' },
  { id: 'fraunces', family: 'Fraunces', category: 'serif', path: 'ofl/fraunces/Fraunces[SOFT,WONK,opsz,wght].ttf', axes: ['SOFT', 'WONK', 'opsz', 'wght'], note: '4 Achsen' },
  { id: 'dm-serif-display', family: 'DM Serif Display', category: 'serif', path: 'ofl/dmserifdisplay/DMSerifDisplay-Regular.ttf' },
  { id: 'instrument-serif', family: 'Instrument Serif', category: 'serif', path: 'ofl/instrumentserif/InstrumentSerif-Regular.ttf' },
  { id: 'libre-baskerville', family: 'Libre Baskerville', category: 'serif', path: 'ofl/librebaskerville/LibreBaskerville[wght].ttf', axes: ['wght'] },
  { id: 'cormorant-garamond', family: 'Cormorant Garamond', category: 'serif', path: 'ofl/cormorantgaramond/CormorantGaramond[wght].ttf', axes: ['wght'] },
  { id: 'cinzel', family: 'Cinzel', category: 'serif', path: 'ofl/cinzel/Cinzel[wght].ttf', axes: ['wght'], note: 'Versalien' },
  { id: 'gloock', family: 'Gloock', category: 'serif', path: 'ofl/gloock/Gloock-Regular.ttf' },

  // ── Mono ────────────────────────────────────────────────────────────────────
  { id: 'jetbrains-mono', family: 'JetBrains Mono', category: 'mono', path: 'ofl/jetbrainsmono/JetBrainsMono[wght].ttf', axes: ['wght'] },
  { id: 'roboto-mono', family: 'Roboto Mono', category: 'mono', path: 'ofl/robotomono/RobotoMono[wght].ttf', axes: ['wght'] },
  { id: 'space-mono', family: 'Space Mono', category: 'mono', path: 'ofl/spacemono/SpaceMono-Regular.ttf' },
  { id: 'ibm-plex-mono', family: 'IBM Plex Mono', category: 'mono', path: 'ofl/ibmplexmono/IBMPlexMono-Regular.ttf' },
  { id: 'reddit-mono', family: 'Reddit Mono', category: 'mono', path: 'ofl/redditmono/RedditMono[wght].ttf', axes: ['wght'] },
  { id: 'major-mono-display', family: 'Major Mono Display', category: 'mono', path: 'ofl/majormonodisplay/MajorMonoDisplay-Regular.ttf', note: 'nur Kleinbuchstaben' },

  // ── Display ─────────────────────────────────────────────────────────────────
  { id: 'anton', family: 'Anton', category: 'display', path: 'ofl/anton/Anton-Regular.ttf', note: 'fette Grotesk' },
  { id: 'bebas-neue', family: 'Bebas Neue', category: 'display', path: 'ofl/bebasneue/BebasNeue-Regular.ttf' },
  { id: 'syne', family: 'Syne', category: 'display', path: 'ofl/syne/Syne[wght].ttf', axes: ['wght'] },
  { id: 'unbounded', family: 'Unbounded', category: 'display', path: 'ofl/unbounded/Unbounded[wght].ttf', axes: ['wght'] },
  { id: 'bricolage', family: 'Bricolage Grotesque', category: 'display', path: 'ofl/bricolagegrotesque/BricolageGrotesque[opsz,wdth,wght].ttf', axes: ['opsz', 'wdth', 'wght'], note: '3 Achsen' },
  { id: 'abril-fatface', family: 'Abril Fatface', category: 'display', path: 'ofl/abrilfatface/AbrilFatface-Regular.ttf' },
  { id: 'righteous', family: 'Righteous', category: 'display', path: 'ofl/righteous/Righteous-Regular.ttf' },
  { id: 'monoton', family: 'Monoton', category: 'display', path: 'ofl/monoton/Monoton-Regular.ttf', note: 'Inline-Linien' },
  { id: 'bungee', family: 'Bungee', category: 'display', path: 'ofl/bungee/Bungee-Regular.ttf' },
  { id: 'rowdies', family: 'Rowdies', category: 'display', path: 'ofl/rowdies/Rowdies-Bold.ttf' },

  // ── Slab ────────────────────────────────────────────────────────────────────
  { id: 'alfa-slab-one', family: 'Alfa Slab One', category: 'slab', path: 'ofl/alfaslabone/AlfaSlabOne-Regular.ttf' },
  { id: 'rubik-mono-one', family: 'Rubik Mono One', category: 'slab', path: 'ofl/rubikmonoone/RubikMonoOne-Regular.ttf' },

  // ── Blackletter ─────────────────────────────────────────────────────────────
  { id: 'unifraktur-maguntia', family: 'UnifrakturMaguntia', category: 'blackletter', path: 'ofl/unifrakturmaguntia/UnifrakturMaguntia-Book.ttf' },
  { id: 'pirata-one', family: 'Pirata One', category: 'blackletter', path: 'ofl/pirataone/PirataOne-Regular.ttf' },

  // ── Experimental (interesting axes to sweep in batch mode) ──────────────────
  { id: 'climate-crisis', family: 'Climate Crisis', category: 'experimental', path: 'ofl/climatecrisis/ClimateCrisis[YEAR].ttf', axes: ['YEAR'], note: 'YEAR-Achse 1979–2050' },
  { id: 'honk', family: 'Honk', category: 'experimental', path: 'ofl/honk/Honk[MORF,SHLN].ttf', axes: ['MORF', 'SHLN'], note: 'MORF/SHLN' },
  { id: 'sixtyfour', family: 'Sixtyfour', category: 'experimental', path: 'ofl/sixtyfour/Sixtyfour[BLED,SCAN].ttf', axes: ['BLED', 'SCAN'], note: 'CRT-Ästhetik' },
  { id: 'nabla', family: 'Nabla', category: 'experimental', path: 'ofl/nabla/Nabla[EDPT,EHLT].ttf', axes: ['EDPT', 'EHLT'], note: 'Extrusionsachsen' },
];

export const FONT_BY_ID = new Map(FONT_CATALOG.map((f) => [f.id, f]));

export const CATEGORY_LABELS: Record<FontCategory, string> = {
  sans: 'Sans',
  serif: 'Serif',
  mono: 'Mono',
  display: 'Display',
  slab: 'Slab',
  blackletter: 'Blackletter',
  experimental: 'Experimentell',
};

export const DEFAULT_FONT_ID = 'anton';
